import { useEffect, useState } from "react";
import { toast } from "sonner";
import CommercialHeader from "@/components/commercial/CommercialHeader";
import { api, fmtEur, fmtDate } from "@/lib/commercialApi";
import { COMMISSION_STATUSES } from "@/lib/commercialStages";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function ComCommissions() {
  const [rows, setRows] = useState(null);
  const [rules, setRules] = useState([]);
  useEffect(() => { load(); api.get("/commission-rules").then((r) => setRules(r.data.rules)); }, []);
  async function load() {
    setRows(null);
    const r = await api.get("/commissions");
    setRows(r.data.commissions);
  }
  async function validate(id) { try { await api.post(`/commissions/${id}/validate`); load(); toast.success("Validée"); } catch { toast.error("Erreur"); } }
  async function pay(id) { try { await api.post(`/commissions/${id}/pay`); load(); toast.success("Marquée comme payée"); } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } }

  return (
    <div className="min-h-screen bg-brand-paper">
      <CommercialHeader />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8">
        <div className="eyebrow mb-1">Rémunération téléprospection</div>
        <h1 className="font-display text-3xl text-brand-ink mb-6">Commissions</h1>

        <div className="rounded-lg border border-brand-line bg-white p-4 mb-6">
          <div className="eyebrow mb-2">Règles actives</div>
          {rules.length === 0 ? (
            <div className="text-xs text-brand-subtle">Règle par défaut : 10 % du montant HT du contrat signé pour la téléprospectrice assignée.</div>
          ) : (
            <div className="space-y-1 text-sm">
              {rules.map((r) => (
                <div key={r.id} className="flex items-center gap-3">
                  <span className="text-brand-ink">{r.name}</span>
                  <span className="text-brand-subtle text-xs">{r.type} · {r.value_type === "percent" ? `${r.value} %` : fmtEur(r.value)} · {r.teleprospectrice || "toutes"}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {rows === null ? <Loader2 className="h-4 w-4 animate-spin text-brand-subtle" />
          : rows.length === 0 ? (
          <div className="rounded-lg border border-brand-line bg-white p-10 text-center max-w-xl mx-auto">
            <div className="font-display text-xl text-brand-ink">Aucune commission</div>
            <p className="mt-2 text-sm text-brand-subtle">
              Les commissions seront générées automatiquement après la signature d'un contrat (règle par défaut : 10 % du montant HT pour la téléprospectrice assignée).
            </p>
            <a href="/commercial/opportunites" className="mt-5 inline-flex items-center h-9 px-4 rounded-md bg-brand-ink text-white text-xs hover:bg-brand-moss">
              Voir le pipeline
            </a>
          </div>
        ) : (
          <div className="rounded-lg border border-brand-line bg-white divide-y divide-brand-line">
            {rows.map((c) => {
              const meta = COMMISSION_STATUSES[c.status] || { label: c.status, color: "" };
              return (
                <div key={c.id} className="p-4 flex items-center justify-between gap-3 flex-wrap">
                  <div className="text-sm">
                    <div className="font-medium">{c.teleprospectrice} · {fmtEur(c.montant_commission)}</div>
                    <div className="text-xs text-brand-subtle mt-0.5">{c.rule_name} · base {fmtEur(c.montant_base)} · {c.periode} · créée le {fmtDate(c.created_at)}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[11px] font-medium px-2.5 py-1 rounded-md border ${meta.color}`}>{meta.label}</span>
                    {c.status === "a_valider" && <Button size="sm" onClick={() => validate(c.id)} data-testid={`com-validate-${c.id}`}>Valider</Button>}
                    {c.status === "validee" && <Button size="sm" variant="outline" onClick={() => pay(c.id)} data-testid={`com-pay-${c.id}`}>Marquer payée</Button>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
